import { Box, Skeleton, Typography, LinearProgress, Chip } from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import { useDashboard } from '../DashboardContext';
import { useTranslation } from 'react-i18next';
import { DashboardInfoTip } from './DashboardInfoTip';
import DashboardWidget from './DashboardWidget';

const CreditRiskAlerts = () => {
  const { creditRiskClients, sectionLoading, currency } = useDashboard();
  const { t } = useTranslation();

  const formatNumber = (num: number) =>
    new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(num) + ` ${currency}`;

  const getColor = (pct: number) => {
    if (pct >= 100) return '#d32f2f';
    if (pct >= 90) return '#e65100';
    return '#ed6c02';
  };

  if (sectionLoading.debt) {
    return (
      <DashboardWidget chartHeight={380}>
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} variant="rectangular" height={48} sx={{ mt: 1.5, borderRadius: 1 }} />
        ))}
      </DashboardWidget>
    );
  }

  return (
    <DashboardWidget
      chartHeight={380}
      scrollable
      header={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon sx={{ color: '#ed6c02' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('common:dashboard.creditRisk.title')}
          </Typography>
          <DashboardInfoTip helpKey="creditRisk.title" />
          {creditRiskClients.length > 0 && (
            <Chip
              label={creditRiskClients.length}
              size="small"
              sx={{ bgcolor: '#d32f2f', color: '#fff', fontWeight: 700, ml: 1 }}
            />
          )}
        </Box>
      }
    >
      {creditRiskClients.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {creditRiskClients.map((client, idx) => (
            <Box
              key={idx}
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: `${getColor(client.utilizationPct)}08`,
                border: `1px solid ${getColor(client.utilizationPct)}30`,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {client.name}
                </Typography>
                <Chip
                  label={`${client.utilizationPct.toFixed(0)}%`}
                  size="small"
                  sx={{
                    bgcolor: getColor(client.utilizationPct),
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.7rem',
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('common:dashboard.creditRisk.balance')}: {formatNumber(client.balance)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('common:dashboard.creditRisk.limit')}: {formatNumber(client.creditLimit)}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(client.utilizationPct, 100)}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: `${getColor(client.utilizationPct)}20`,
                  '& .MuiLinearProgress-bar': { bgcolor: getColor(client.utilizationPct), borderRadius: 3 },
                }}
              />
            </Box>
          ))}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary' }}>
          <Typography variant="body2">{t('common:dashboard.creditRisk.noRisk')}</Typography>
        </Box>
      )}
    </DashboardWidget>
  );
};

export default CreditRiskAlerts;